/**
 * EP-160 section 2.2: the refusals a FlowRent support session gets from the
 * operator API, read from a failed request.
 *
 * - 403 support_session_blocked: the deny list (SupportSession::Blocked)
 *   refuses the page or the action. queryClient.ts throws it to the page's
 *   error boundary (SupportSessionBlockedBoundary), which shows the blocked
 *   page.
 * - 422 masked_value_in_write: the body carried a masked value ("ส•••",
 *   "xxx-xxx-1234") or a hidden-field marker (details.paths names them).
 * - 422 scope_setup_no_tenants: the setup scope enters and changes no tenant
 *   data, and confirms no customers or leases tab of an import.
 *
 * translateApiError (apiError.ts) turns each into one readable sentence from
 * common:support_session.errors.*. Re-exported by api.ts. No axios import:
 * the error is read by its shape, so tests that replace axios or api.ts still
 * get these helpers.
 */
export const SUPPORT_SESSION_ERROR_CODES = [
  'support_session_blocked',
  'masked_value_in_write',
  'scope_setup_no_tenants',
] as const
export type SupportSessionErrorCode = (typeof SUPPORT_SESSION_ERROR_CODES)[number]

function field(value: unknown, name: string): unknown {
  return value !== null && typeof value === 'object' ? Reflect.get(value, name) : undefined
}

/** The `{ error: { code } }` of a failed request, if the server sent one. */
export function apiErrorCode(err: unknown): string | undefined {
  const code = field(field(field(field(err, 'response'), 'data'), 'error'), 'code')
  return typeof code === 'string' ? code : undefined
}

export function supportSessionErrorCode(err: unknown): SupportSessionErrorCode | undefined {
  const code = apiErrorCode(err)
  return SUPPORT_SESSION_ERROR_CODES.find((known) => known === code)
}

export function isSupportSessionBlocked(err: unknown): boolean {
  return supportSessionErrorCode(err) === 'support_session_blocked'
}

/**
 * True when the failed request searched (a non-empty q or search parameter).
 * In the read and setup scopes the server refuses a search by name or phone
 * on a customer-bearing list with support_session_blocked: that refuses the
 * search, not the page, so it is not thrown to the page boundary.
 */
export function isSearchRequest(err: unknown): boolean {
  const params = field(field(err, 'config'), 'params')
  return ['q', 'search'].some((name) => {
    const value = field(params, name)
    return typeof value === 'string' ? value.trim() !== '' : value != null
  })
}
